import { useState, type Dispatch, type SetStateAction } from "react";
import type { CollabPreview } from "@cabal/shared/domain/session/collab/types.js";
import type { ReflectionPreview } from "@cabal/shared/domain/session/reflection/types.js";
import type {
  ProductDetailResponse,
  ProductSummary,
} from "@cabal/shared/domain/session/product/types.js";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import type { UserShortIdView } from "@cabal/shared/domain/session/user-short-id/types.js";
import type {
  VersionDetailResponse,
  VersionPreview,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import {
  ChatMode,
  type ChatState,
  type LoadingStates,
} from "@/types/frontend-types";
import type { SessionCollab, SessionTeamDetails } from "./context";

type StateSetter<T> = Dispatch<SetStateAction<T>>;

interface SessionProviderMetaState {
  urlRootProductId: string | null;
  setUrlRootProductId: StateSetter<string | null>;
  teamDefaultStartingRounds: number | null;
  setTeamDefaultStartingRounds: StateSetter<number | null>;
  teamDetails: SessionTeamDetails | null;
  setTeamDetails: StateSetter<SessionTeamDetails | null>;
  loading: LoadingStates;
  setLoading: StateSetter<LoadingStates>;
  notFound: boolean;
  setNotFound: StateSetter<boolean>;
}

interface SessionProviderEntityState {
  products: Record<string, ProductSummary>;
  setProducts: StateSetter<Record<string, ProductSummary>>;
  versions: Record<string, VersionSummary>;
  setVersions: StateSetter<Record<string, VersionSummary>>;
  productDetailsById: Record<string, ProductDetailResponse>;
  setProductDetailsById: StateSetter<Record<string, ProductDetailResponse>>;
  versionDetailsById: Record<string, VersionDetailResponse>;
  setVersionDetailsById: StateSetter<Record<string, VersionDetailResponse>>;
  members: Record<string, Member>;
  setMembers: StateSetter<Record<string, Member>>;
  versionPreviews: Record<string, VersionPreview>;
  setVersionPreviews: StateSetter<Record<string, VersionPreview>>;
  collabPreviews: Record<string, CollabPreview>;
  setCollabPreviews: StateSetter<Record<string, CollabPreview>>;
  reflectionPreviews: Record<string, ReflectionPreview>;
  setReflectionPreviews: StateSetter<Record<string, ReflectionPreview>>;
  userShortIdViews: UserShortIdView[];
  setUserShortIdViews: StateSetter<UserShortIdView[]>;
}

interface SessionProviderConversationState {
  chatMessages: Record<string, SessionCollab>;
  setChatMessages: StateSetter<Record<string, SessionCollab>>;
  collabs: Record<string, SessionCollab>;
  setCollabs: StateSetter<Record<string, SessionCollab>>;
  chatMessageIds: string[];
  setChatMessageIds: StateSetter<string[]>;
  chatCursor: string | null;
  setChatCursor: StateSetter<string | null>;
  chatHasMore: boolean;
  setChatHasMore: StateSetter<boolean>;
  chatLoadingMore: boolean;
  setChatLoadingMore: StateSetter<boolean>;
  collabsByProduct: Record<string, string[]>;
  setCollabsByProduct: StateSetter<Record<string, string[]>>;
}

interface SessionProviderInteractionState {
  chat: ChatState;
  setChat: StateSetter<ChatState>;
  newMessageIds: Set<string>;
  setNewMessageIds: StateSetter<Set<string>>;
}

export function useSessionProviderMetaState(): SessionProviderMetaState {
  const [urlRootProductId, setUrlRootProductId] = useState<string | null>(null);
  const [teamDefaultStartingRounds, setTeamDefaultStartingRounds] = useState<
    number | null
  >(null);
  const [teamDetails, setTeamDetails] = useState<SessionTeamDetails | null>(
    null,
  );
  const [loading, setLoading] = useState<LoadingStates>({
    session: true,
    products: true,
    members: true,
    chat: true,
  });
  const [notFound, setNotFound] = useState(false);

  return {
    urlRootProductId,
    setUrlRootProductId,
    teamDefaultStartingRounds,
    setTeamDefaultStartingRounds,
    teamDetails,
    setTeamDetails,
    loading,
    setLoading,
    notFound,
    setNotFound,
  };
}

export function useSessionProviderEntityState(): SessionProviderEntityState {
  const [products, setProducts] = useState<Record<string, ProductSummary>>({});
  const [versions, setVersions] = useState<Record<string, VersionSummary>>({});
  const [productDetailsById, setProductDetailsById] = useState<
    Record<string, ProductDetailResponse>
  >({});
  const [versionDetailsById, setVersionDetailsById] = useState<
    Record<string, VersionDetailResponse>
  >({});
  const [members, setMembers] = useState<Record<string, Member>>({});
  const [versionPreviews, setVersionPreviews] = useState<
    Record<string, VersionPreview>
  >({});
  const [collabPreviews, setCollabPreviews] = useState<
    Record<string, CollabPreview>
  >({});
  const [reflectionPreviews, setReflectionPreviews] = useState<
    Record<string, ReflectionPreview>
  >({});
  const [userShortIdViews, setUserShortIdViews] = useState<UserShortIdView[]>(
    [],
  );

  return {
    products,
    setProducts,
    versions,
    setVersions,
    productDetailsById,
    setProductDetailsById,
    versionDetailsById,
    setVersionDetailsById,
    members,
    setMembers,
    versionPreviews,
    setVersionPreviews,
    collabPreviews,
    setCollabPreviews,
    reflectionPreviews,
    setReflectionPreviews,
    userShortIdViews,
    setUserShortIdViews,
  };
}

export function useSessionProviderConversationState(): SessionProviderConversationState {
  const [chatMessages, setChatMessages] = useState<
    Record<string, SessionCollab>
  >({});
  const [collabs, setCollabs] = useState<Record<string, SessionCollab>>({});
  const [chatMessageIds, setChatMessageIds] = useState<string[]>([]);
  const [chatCursor, setChatCursor] = useState<string | null>(null);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatLoadingMore, setChatLoadingMore] = useState(false);
  const [collabsByProduct, setCollabsByProduct] = useState<
    Record<string, string[]>
  >({});

  return {
    chatMessages,
    setChatMessages,
    collabs,
    setCollabs,
    chatMessageIds,
    setChatMessageIds,
    chatCursor,
    setChatCursor,
    chatHasMore,
    setChatHasMore,
    chatLoadingMore,
    setChatLoadingMore,
    collabsByProduct,
    setCollabsByProduct,
  };
}

export function useSessionProviderInteractionState(): SessionProviderInteractionState {
  const [chat, setChat] = useState<ChatState>({
    mode: ChatMode.Main,
    selectedProductId: null,
    targetVersionId: null,
    targetMessageId: null,
    targetRequestId: 0,
    indicatedMessageId: null,
    indicationRequestId: 0,
  });
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(new Set());

  return {
    chat,
    setChat,
    newMessageIds,
    setNewMessageIds,
  };
}
